/**
 * HOTTR. worlds — the variable layer of the identity system.
 *
 * The ecosystem problem this solves: thirteen products have to read as one
 * family without reading as thirteen copies of the same app. That is not a
 * dial between "identical" and "different" — it is two layers, and this file
 * is the one that is allowed to vary.
 *
 *   FIXED  (kit + tokens, never per-product): the HOTTR. wordmark construction,
 *          ink canvas, pink-only accent, Poppins, 2px geometry, neon-bloom
 *          shadows, the mechanical ease, an Orb in every product icon.
 *
 *   VARIABLE (this file): what the Orb is DOING, how the world moves, what it
 *          photographs, and how it feels.
 *
 * One decision per product, made once, expressed at every size. That is what
 * makes the set read as deliberate rather than as thirteen separate design
 * exercises — and it is why `verb` is a single word. If a product cannot be
 * reduced to one verb, its icon will not survive being drawn at 16px.
 *
 * Source: HOTTR_Worlds_design.md §8 (icon logic + motion verbs) and §10
 * (world image direction). Keep this table and that document in step.
 *
 * The Screen System (brand/HOTTR_Screen_System.md §8) added a second axis to
 * this table: the route vocabulary each world puts in the fan and the rail.
 * The design board carried that as a separate worlds.json; it is merged here
 * instead, because the same facts in two files is precisely the drift this
 * repo exists to stop. Route data is populated ONLY for the worlds that board
 * actually defined — the rest are `undefined`, not invented.
 *
 * Note what is deliberately NOT stored here: the Orb's per-product hook class.
 * HottrOrb derives `hottr-orb--product-<name>` from its `product` prop, so a
 * field for it would be a second copy of a string the component already builds.
 * Worlds with no distinct light (app, brand) simply match no rule in
 * hottr-orb.css, which is a harmless no-op.
 */

export type HottrProduct =
  | 'app'
  | 'tv'
  | 'men'
  | 'travel'
  | 'brand'
  | 'care'
  | 'chat'
  | 'me'
  | 'media'
  | 'music'
  | 'news'
  | 'pro'
  | 'careers'
  | 'passport'
  | 'world';

export interface World {
  /** The lockup suffix. Rendered identically for every product — see HottrLockup. */
  suffix: HottrProduct;
  /** What the product is, in one line. */
  role: string;
  /**
   * What the Orb DOES here. One word, present tense. This is the product's
   * single identity decision: the icon draws it, the motion performs it, and
   * the imagery should feel like it.
   */
  verb: string;
  /** How the icon is constructed around the Orb. */
  iconLogic: string;
  /** The feeling the imagery must produce. Never "none" — an emotionally blank frame has failed. */
  emotion: string;
  /** What the photography shows. */
  subject: string;
  /** The lighting signature. */
  light: string;
  /**
   * The world's own route vocabulary, in fan order furthest-from-Orb first.
   * FIXED_TAIL_ROUTES is appended by OrbFan/OrbRail and must NOT be repeated
   * here — a world that lists its own `passport` would render it twice.
   *
   * `undefined` means the Screen System board never defined one for this
   * world. That is a real state, not a gap to fill with a plausible guess:
   * inventing a menu here would ship it to a live app on the next sync.
   */
  routes?: string[];
  /**
   * Example context actions — the 1–3 actions that sit above the fan's
   * hairline, keyed by the route or object they belong to. Illustrative, not
   * exhaustive; the product decides its own at runtime.
   */
  context?: Record<string, string[]>;
  /**
   * Where this world actually lives.
   *
   * Consumers previously built this as `https://hottr.${suffix}`, which is
   * right for most worlds and wrong for the ones that matter most to get
   * right: Passport is `passport.hottr.world`, the brand reference site is
   * `brand.hottr.world`, and the gateway is the bare `hottr.world`. A switcher
   * that sends someone to a domain that does not exist is worse than one that
   * omits the entry.
   *
   * Absent means the world has no live home yet. `worldHref` returns null and
   * the switcher renders it as unavailable rather than as a dead link.
   */
  domain?: string;
}

/** Which side the Orb and its fan live on. One Passport setting flips both. */
export type Hand = 'left' | 'right';

/**
 * Routes never change order and always end with the worlds door then HOTTIE,
 * so the bottom of the list is learned once and holds in every world.
 *
 * The route KEYS stay `passport` / `hottie` — icons, styles, behaviour and
 * every consumer all key off them, and renaming a key is a migration, not a
 * copy change. What a person READS is routeLabel()'s business.
 *
 * Appended by the components rather than stored per world: a world that could
 * forget its tail is a world that eventually does.
 */
export const FIXED_TAIL_ROUTES = ['passport', 'hottie'] as const;

/**
 * Labels for routes whose key and words differ.
 *
 * `passport` is the only entry: the key is historical, the door it opens is
 * the header's worlds picker, and the internal name "Passport" is never
 * user-facing — a reader sees WORLDS. Everything else renders its own name.
 */
export const TAIL_LABELS: Record<string, string> = { passport: 'Worlds' };

/** The words on screen for a route. Falls back to the route key itself. */
export const routeLabel = (route: string): string => TAIL_LABELS[route] ?? route;

/** Right-handed unless Passport says otherwise. */
export const HAND_DEFAULT: Hand = 'right';

export const WORLDS: Record<HottrProduct, World> = {
  app: {
    suffix: 'app',
    domain: 'https://hottr.app',
    role: 'Connection, matching, conversation and discovery',
    verb: 'connects',
    iconLogic: 'Orb attracting and connecting people or signals',
    emotion: 'Chemistry, possibility, immediacy',
    subject: 'People in moments of attraction, conversation or discovery',
    light: 'Intimate neon edge light',
    routes: ['feed', 'people', 'places', 'messages'],
    context: { people: ['filter', 'switch to grid'] },
  },
  tv: {
    suffix: 'tv',
    domain: 'https://hottr.tv',
    role: 'Video, creators, broadcast and entertainment',
    verb: 'focuses',
    iconLogic: 'Orb as the focal element of a lens or broadcast aperture',
    emotion: 'Anticipation, spectacle, immersion',
    subject: 'Creators, cameras, stages and live production',
    light: 'High-contrast cinematic light',
    routes: ['live', 'shows', 'library', 'messages'],
  },
  men: {
    suffix: 'men',
    domain: 'https://hottr.men',
    role: 'Provider discovery, selection, availability and booking',
    verb: 'selects',
    iconLogic: 'Orb inside a precision selection or diamond structure',
    emotion: 'Desire, confidence, choice',
    subject: 'Confident portraits and controlled selection moments',
    light: 'Sculpted warm light with pink rim',
    routes: ['feed', 'people', 'places', 'messages'],
    context: {
      people: ['filter', 'switch to grid'],
      callingCard: ['save', 'share', 'block'],
    },
  },
  travel: {
    suffix: 'travel',
    domain: 'https://hottr.travel',
    role: 'Destinations, movement, experiences and discovery',
    verb: 'moves',
    iconLogic: 'Orb acting as a beacon moving through a route or orbit',
    emotion: 'Freedom, curiosity, escape',
    subject: 'Movement, destination and human discovery',
    light: 'Expansive natural light with pink beacon accents',
    routes: ['destinations', 'trips', 'stays', 'messages'],
    context: { destinations: ['add to trip'], stay: ['book', 'save'] },
  },
  brand: {
    suffix: 'brand',
    domain: 'https://brand.hottr.world',
    role: 'Design system, assets, standards and governance',
    verb: 'aligns',
    iconLogic: 'Orb locking into a modular construction grid',
    emotion: 'Precision, control, intention',
    subject: 'Systems, interfaces, materials and construction',
    light: 'Controlled studio light',
    routes: ['guidelines', 'kit', 'worlds'],
    // The reference site has no assistant behind it, so it is the one
    // documented place HOTTIE drops off the tail. OrbFan/OrbRail take that as
    // an explicit prop rather than reading it from here — see FIXED_TAIL_ROUTES.
  },
  care: {
    suffix: 'care',
    domain: 'https://www.hottr.care',
    role: 'Support, conversation, wellbeing and guidance',
    verb: 'breathes',
    iconLogic: 'Orb held or protected by an open supportive form',
    emotion: 'Trust, warmth, relief',
    subject: 'Human presence, listening and emotional support',
    light: 'Warm low-key interiors',
    /**
     * AUTHORED during the chassis rollout, not taken from the Screen System
     * board — .care shipped before the board defined a vocabulary for it, and
     * without one `worldRoutes('care')` returned only the fixed tail, so its
     * whole menu was the worlds door and HOTTIE.
     *
     * These are the app's five real destinations, in the order its own sidebar
     * already used. TODAY is first because it is where an authed person lands.
     * PRICING is deliberately absent: it is a conversion surface, not a room
     * you navigate between, and the app already drops it on mobile.
     */
    routes: ['today', 'history', 'journey', 'settings'],
    context: { today: ['talk to hottie'], history: ['filter'] },
  },
  /**
   * chat closes the gap the Screen System §8 flagged: the sync has shipped to
   * ../HOTTR.chat for months with no world entry at all.
   *
   * AUTHORED, NOT SOURCED. The design board supplied only `verb: replies`, the
   * routes, and the Orb light (highlight 40%/26%, crescent -8deg, now in
   * hottr-orb.css). Every other world's identity lines come from
   * HOTTR_Worlds_design.md §8/§10; chat's five below were written during the
   * 2026-08-12 Screen System import to satisfy the interface, in the voice of
   * the rest of the table. They are the weakest rows here — review before they
   * set, because per §8 one verb has to carry the icon at 16px.
   *
   * The reasoning, so a reviewer can argue with it rather than guess at it:
   * the board describes chat's light as "low and close, reading as presence
   * rather than a search or a signal". Presence is the through-line — chat is
   * the world where someone is already there, so the Orb answers rather than
   * seeks, and the imagery is the near distance, not the reach.
   */
  chat: {
    suffix: 'chat',
    domain: 'https://hottr.chat',
    role: 'Threads, requests and sustained one-to-one conversation',
    verb: 'replies',
    iconLogic: 'Orb answering — a second form turned toward it, already close',
    emotion: 'Presence, intimacy, being answered',
    subject: 'Two people mid-exchange, the near distance between them',
    light: 'Close low light, pink from just off-frame',
    routes: ['threads', 'requests'],
  },
  me: {
    suffix: 'me',
    domain: 'https://hottr.me',
    role: 'Personal growth, behaviour change and agency',
    verb: 'moves',
    iconLogic: 'Orb as a forward step breaking a closed loop',
    emotion: 'Agency, momentum, self-command',
    subject: 'Real-life movement, decision points and return to presence',
    light: 'Direct neon edge light — no soft wellness wash',
    /**
     * AUTHORED during the chassis rollout, not taken from the Screen System
     * board — the same gap .care had. Without this, `worldRoutes('me')` is
     * only the fixed tail, so the Orb hides the three rooms the app already
     * has. HOTTR.me#3 removed a runtime `WORLDS.me.routes ??=` patch rather
     * than invent the menu in the consumer; the names belong here.
     *
     * These are the app's three product rooms, in fan order furthest-from-Orb
     * first. TODAY is first because it is where an authed person lands.
     * PATHS and glyphs for these names stay in HOTTR.me; this table only
     * names the Orb vocabulary. The worlds door and HOTTIE are the fixed
     * tail, not listed here — same as every other world.
     */
    routes: ['today', 'breakout', 'momentum'],
  },
  media: {
    suffix: 'media',
    role: 'Production, campaigns, photography and video',
    verb: 'frames',
    iconLogic: 'Orb as the capture point inside a production frame',
    emotion: 'Creativity, capability, momentum',
    subject: 'Cameras, crews, sets and post-production',
    light: 'Production lighting and practicals',
  },
  music: {
    suffix: 'music',
    domain: 'https://hottr.music',
    role: 'Music creation, releases, clips and performance',
    verb: 'reacts',
    iconLogic: 'Orb generating or responding to a waveform',
    emotion: 'Energy, rhythm, release',
    subject: 'Performance, studios, crowds and sound energy',
    light: 'Rhythmic stage light',
    routes: ['feed', 'catalogue', 'playlists', 'messages'],
    context: { release: ['queue', 'shuffle'] },
  },
  news: {
    suffix: 'news',
    domain: 'https://hottr.news',
    role: 'Current affairs, reporting, culture and live information',
    verb: 'transmits',
    iconLogic: 'Orb transmitting live information through a signal structure',
    emotion: 'Relevance, urgency, awareness',
    subject: 'Real events, reporting and people affected by the story',
    light: 'Editorial, contextual, mood-appropriate',
    routes: ['latest', 'topics', 'saved'],
    context: { story: ['follow topic', 'save story'] },
  },
  pro: {
    suffix: 'pro',
    role: 'Performers, actors, entertainers and talent discovery',
    verb: 'reveals',
    iconLogic: 'Orb revealed by a spotlight or stage architecture',
    emotion: 'Visibility, ambition, performance',
    subject: 'Talent on stage, in rehearsal or being discovered',
    light: 'Spotlight and performance haze',
  },
  careers: {
    suffix: 'careers',
    role: 'Work, opportunity, workplace rights and progression',
    verb: 'opens',
    iconLogic: 'Orb as light passing through an open doorway',
    emotion: 'Opportunity, progress, hope',
    subject: 'Thresholds, workplaces and forward movement',
    light: 'Doorway or directional light',
  },
  passport: {
    suffix: 'passport',
    domain: 'https://passport.hottr.world',
    role: 'Identity, verification, permissions and secure access',
    verb: 'verifies',
    iconLogic: 'Orb secured inside a verification ring or identity seal',
    emotion: 'Trust, control, certainty',
    subject: 'Identity, access and verification interactions',
    light: 'Precise controlled light',
  },
  world: {
    suffix: 'world',
    domain: 'https://hottr.world',
    role: 'The gateway connecting the entire ecosystem',
    verb: 'expands',
    iconLogic: 'Orb at the centre of an interconnected network',
    emotion: 'Belonging, scale, connection',
    subject: 'Connected places, people and product nodes',
    light: 'Spatial network light',
  },
};

export const PRODUCTS = Object.keys(WORLDS) as HottrProduct[];

/** Narrowing helper for values arriving from config, env or a route param. */
export const isProduct = (v: unknown): v is HottrProduct =>
  typeof v === 'string' && v in WORLDS;

/**
 * A world's full route list: its own vocabulary, then the fixed tail.
 *
 * Both OrbFan and OrbRail need this, and both getting it slightly wrong in
 * different ways is exactly how the ecosystem ended up with two headers that
 * agreed on nothing. One function, one answer.
 *
 * `hottie: false` drops the assistant from the tail — for the brand reference
 * site, which has none. The worlds door always stays: every surface can move.
 *
 * `omit` drops a world route the app has not built yet.
 *
 * This exists because the table above is the world's INTENDED vocabulary, and
 * an app is often partway to it: `.men` has no PLACES or MESSAGES destination,
 * `.travel` has no MESSAGES. Without a way to hide them, the fan renders a
 * control that closes and goes nowhere — and a menu item that lies is worse
 * than a menu that is short, because the user concludes the product is broken
 * rather than unfinished.
 *
 * Deliberately a subtraction and not a replacement: an app can hide a word it
 * cannot serve, but it cannot invent one. That is what keeps LATEST meaning
 * the same thing on every site.
 */
/**
 * Where to send someone for a world, or `null` if it has no live home.
 *
 * Returning null rather than a guessed URL is the point. `https://hottr.${suffix}`
 * looks correct and silently produces dead addresses for Passport, the brand
 * site, the gateway and every unlaunched product — and a switcher entry that
 * 404s is worse than no entry, because the person concludes the whole ecosystem
 * is broken rather than that one room is not built yet.
 */
export const worldHref = (product: HottrProduct): string | null =>
  WORLDS[product].domain ?? null;

/**
 * Worlds that can start Passport SSO on arrival (`/auth/hop`).
 *
 * A hop URL must only be offered where that route exists — and since the One
 * HOTTR sign-in, that is all nine live consumer worlds. The internal name and
 * the locked front door never get one: a hop into a page that is staying shut
 * is a dead door wearing a key.
 */
export const PASSPORT_HOP_WORLDS: readonly HottrProduct[] = [
  'app',
  'tv',
  'men',
  'travel',
  'me',
  'music',
  'care',
  'chat',
  'news',
];

/**
 * Where the wordmark switcher should send someone so a known Passport
 * session can land already signed in. PKCE must start on the DESTINATION,
 * so this is still a B URL (`/auth/hop`), never passport.hottr.world/sso.
 */
export const worldHopHref = (product: HottrProduct): string | null => {
  const home = worldHref(product);
  if (!home) return null;
  if (!PASSPORT_HOP_WORLDS.includes(product)) return home;
  return `${home.replace(/\/$/, '')}/auth/hop`;
};

/**
 * This world's home room — the first of its own routes, never WORLDS/HOTTIE.
 *
 * In-world navigation, not a world switch (`worldHref` is a different domain).
 * The Orb's tap does not call this — tap opens the submenu in place.
 * `null` means this world has no room of its own yet.
 */
export const worldHome = (
  product: HottrProduct,
  omit: readonly string[] = [],
): string | null => {
  const own = (WORLDS[product].routes ?? []).filter((r) => !omit.includes(r));
  return own[0] ?? null;
};

/** The worlds that currently have somewhere to go. */
export const LIVE_WORLDS: HottrProduct[] = (Object.keys(WORLDS) as HottrProduct[]).filter(
  (p) => Boolean(WORLDS[p].domain),
);

/**
 * The worlds a product's picker offers by default: the nine live consumer
 * products, in canon order.
 *
 * LIVE_WORLDS is a fact ("has a live address"); this is a decision ("is a
 * place a person is being offered"). They differ in exactly the ways that
 * matter: Passport is an internal name and never surfaces; the brand
 * reference site and the locked gateway are not consumer destinations; and
 * the unlaunched worlds have nothing to open. Defaulting the switcher to the
 * fact is how nine apps each ended up leaking a different opinion.
 *
 * A consumer may still pass its own list — but it should have a reason.
 */
export const SWITCHABLE_WORLDS: HottrProduct[] = [
  'app',
  'tv',
  'men',
  'travel',
  'care',
  'chat',
  'me',
  'music',
  'news',
];

export const worldRoutes = (
  product: HottrProduct,
  { hottie = true, omit = [] }: { hottie?: boolean; omit?: readonly string[] } = {},
): string[] => {
  const own = (WORLDS[product].routes ?? []).filter((r) => !omit.includes(r));
  const tail = FIXED_TAIL_ROUTES.filter((r) => hottie || r !== 'hottie');
  return [...own, ...tail];
};
